import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  UserPlus,
  Prohibit,
  ArrowClockwise,
  CheckCircle,
  X,
  Envelope,
  Buildings,
  ShieldStar,
  MagnifyingGlass,
  Copy,
} from "@phosphor-icons/react";

const ROLES = ["admin", "accountant", "viewer"];

export default function AdminUsersPage() {
  const { user, formatApiError } = useAuth();

  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Invite dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ email: "", name: "", company_id: "__none__", role: "admin" });
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  // Assign company dialog
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null); // user obj
  const [assign, setAssign] = useState({ company_id: "", role: "admin" });

  const isSuperadmin = user && user.is_superadmin;

  const load = async () => {
    setLoading(true);
    try {
      const [uRes, cRes] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/companies"),
      ]);
      setUsers(uRes.data);
      setCompanies(cRes.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperadmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.email?.toLowerCase().includes(q) ||
        u.name?.toLowerCase().includes(q) ||
        u.memberships?.some((m) => m.company_name?.toLowerCase().includes(q))
    );
  }, [users, search]);

  const stats = useMemo(
    () => ({
      total: users.length,
      superadmins: users.filter((u) => u.is_superadmin).length,
      blocked: users.filter((u) => u.blocked).length,
      invited: users.filter((u) => u.invited).length,
    }),
    [users]
  );

  if (user === null) return null;
  if (!isSuperadmin) return <Navigate to="/dashboard" replace />;

  const sendInvite = async () => {
    if (!invite.email) return toast.error("Email is required");
    setInviteBusy(true);
    try {
      const payload = {
        email: invite.email.trim(),
        name: invite.name.trim() || undefined,
        role: invite.role,
      };
      if (invite.company_id && invite.company_id !== "__none__") payload.company_id = invite.company_id;
      const { data } = await api.post("/admin/invite", payload);
      setInviteResult(data);
      if (data.email_status?.sent) {
        toast.success(`Invite emailed to ${invite.email}`);
      } else {
        toast.warning("Invite created but email failed to send — copy the link manually");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invite failed");
    } finally {
      setInviteBusy(false);
    }
  };

  const resendInvite = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/resend-invite`);
      if (data.email_status?.sent) {
        toast.success(`Resent invite to ${u.email}`);
      } else {
        toast.warning("Invite created but email failed to send. Link: " + data.invite_link);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const block = async (u) => {
    if (!window.confirm(`Block ${u.email}? Their sessions will be terminated immediately.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/block`, {});
      toast.success(`${u.email} blocked`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const unblock = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/unblock`, {});
      toast.success(`${u.email} unblocked`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const openAssign = (u) => {
    setAssignTarget(u);
    setAssign({ company_id: companies[0]?.id || "", role: "admin" });
    setAssignOpen(true);
  };

  const doAssign = async () => {
    if (!assign.company_id) return toast.error("Pick a company");
    try {
      await api.post(`/admin/users/${assignTarget.id}/memberships`, assign);
      toast.success(`Assigned to company`);
      setAssignOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const revoke = async (u, mid) => {
    if (!window.confirm(`Revoke this user's access to the company?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}/memberships/${mid}`);
      toast.success("Access revoked");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Master Admin"
        title="Users & Access"
        description="Provision users, assign companies, revoke access, and send magic-link invitations."
        actions={
          <Button
            data-testid="admin-invite-btn"
            onClick={() => {
              setInvite({ email: "", name: "", company_id: "__none__", role: "admin" });
              setInviteResult(null);
              setInviteOpen(true);
            }}
            className="gap-2"
          >
            <UserPlus size={16} weight="bold" />
            Invite User
          </Button>
        }
      />

      <PageBody>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <StatCard label="Total users" value={stats.total} icon={ShieldStar} testId="admin-stat-total" />
          <StatCard label="Master admins" value={stats.superadmins} icon={ShieldStar} testId="admin-stat-super" />
          <StatCard label="Blocked" value={stats.blocked} icon={Prohibit} tone="destructive" testId="admin-stat-blocked" />
          <StatCard label="Pending invites" value={stats.invited} icon={Envelope} testId="admin-stat-invited" />
        </div>

        <div className="mb-3 flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email, name or company…"
              className="pl-9"
              data-testid="admin-search-input"
            />
          </div>
          <div className="text-xs text-muted-foreground">
            Showing {filteredUsers.length} of {users.length}
          </div>
        </div>

        <div className="qb-card overflow-hidden">
          <table className="qb-table w-full" data-testid="admin-users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Status</th>
                <th>Companies</th>
                <th className="text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-muted-foreground">Loading…</td>
                </tr>
              )}
              {!loading && filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-muted-foreground">No users found</td>
                </tr>
              )}
              {filteredUsers.map((u) => (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                  <td>
                    <div className="font-medium">
                      {u.name || <span className="text-muted-foreground">—</span>}
                      {u.is_superadmin && (
                        <span className="ml-2 tag-badge" style={{ background: "hsl(var(--primary))", color: "white" }}>
                          MASTER
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                  </td>
                  <td>
                    {u.blocked ? (
                      <span className="tag-badge" style={{ background: "hsl(var(--destructive))", color: "white" }}>BLOCKED</span>
                    ) : u.invited ? (
                      <span className="tag-badge">INVITED</span>
                    ) : (
                      <span className="tag-badge" style={{ background: "hsl(var(--primary))", color: "white" }}>ACTIVE</span>
                    )}
                    {u.blocked_reason && (
                      <div className="text-[10px] text-muted-foreground mt-1 max-w-[160px] truncate" title={u.blocked_reason}>
                        {u.blocked_reason}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1.5">
                      {(u.memberships || []).length === 0 && (
                        <span className="text-xs text-muted-foreground">No companies</span>
                      )}
                      {(u.memberships || []).map((m) => (
                        <span
                          key={m.id}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[11px]"
                          style={{ borderColor: "hsl(var(--border))" }}
                          data-testid={`admin-mem-${m.id}`}
                        >
                          <Buildings size={12} />
                          {m.company_name || m.company_id}
                          <span className="text-muted-foreground">· {m.role}</span>
                          <button
                            className="ml-1 hover:text-destructive"
                            onClick={() => revoke(u, m.id)}
                            data-testid={`admin-mem-revoke-${m.id}`}
                            title="Revoke access"
                          >
                            <X size={11} />
                          </button>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="text-right pr-4">
                    <div className="inline-flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openAssign(u)}
                        data-testid={`admin-assign-${u.id}`}
                      >
                        <Buildings size={13} className="mr-1" />
                        Assign
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resendInvite(u)}
                        data-testid={`admin-resend-${u.id}`}
                        title="Send a fresh magic-link"
                      >
                        <ArrowClockwise size={13} className="mr-1" />
                        Invite
                      </Button>
                      {u.blocked ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => unblock(u)}
                          data-testid={`admin-unblock-${u.id}`}
                        >
                          <CheckCircle size={13} className="mr-1" />
                          Unblock
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={u.is_superadmin && u.id === user.id}
                          onClick={() => block(u)}
                          data-testid={`admin-block-${u.id}`}
                        >
                          <Prohibit size={13} className="mr-1" />
                          Block
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PageBody>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="admin-invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite a new user</DialogTitle>
            <DialogDescription>
              A magic-link email will be sent so the user can set their own password.
            </DialogDescription>
          </DialogHeader>

          {inviteResult ? (
            <div className="space-y-3">
              <div className="p-3 rounded-md bg-muted/60 border">
                <div className="text-xs text-muted-foreground mb-1">Invite link (expires in 72 hours)</div>
                <div className="flex items-center gap-2">
                  <code className="text-[11px] break-all flex-1">{inviteResult.invite_link}</code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(inviteResult.invite_link);
                      toast.success("Link copied");
                    }}
                    data-testid="admin-invite-copy-link"
                  >
                    <Copy size={13} />
                  </Button>
                </div>
              </div>
              <div className="text-xs">
                Email delivery:{" "}
                {inviteResult.email_status?.sent ? (
                  <span className="text-primary">Sent ({inviteResult.email_status.resend_id})</span>
                ) : (
                  <span className="text-destructive">Failed — {inviteResult.email_status?.detail}</span>
                )}
              </div>
              <DialogFooter>
                <Button
                  onClick={() => setInviteOpen(false)}
                  data-testid="admin-invite-close"
                >
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label>Email</Label>
                <Input
                  value={invite.email}
                  onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                  placeholder="jane@company.com"
                  data-testid="admin-invite-email"
                />
              </div>
              <div>
                <Label>Full name (optional)</Label>
                <Input
                  value={invite.name}
                  onChange={(e) => setInvite({ ...invite, name: e.target.value })}
                  placeholder="Jane Doe"
                  data-testid="admin-invite-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Assign to company (optional)</Label>
                  <Select
                    value={invite.company_id}
                    onValueChange={(v) => setInvite({ ...invite, company_id: v })}
                  >
                    <SelectTrigger data-testid="admin-invite-company">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Role</Label>
                  <Select
                    value={invite.role}
                    onValueChange={(v) => setInvite({ ...invite, role: v })}
                  >
                    <SelectTrigger data-testid="admin-invite-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setInviteOpen(false)}>Cancel</Button>
                <Button
                  onClick={sendInvite}
                  disabled={inviteBusy || !invite.email}
                  data-testid="admin-invite-send"
                >
                  {inviteBusy ? "Sending…" : "Send invite"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign company dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="admin-assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign company access</DialogTitle>
            <DialogDescription>
              {assignTarget?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Company</Label>
              <Select
                value={assign.company_id}
                onValueChange={(v) => setAssign({ ...assign, company_id: v })}
              >
                <SelectTrigger data-testid="admin-assign-company">
                  <SelectValue placeholder="Pick a company" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={assign.role}
                onValueChange={(v) => setAssign({ ...assign, role: v })}
              >
                <SelectTrigger data-testid="admin-assign-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={doAssign} data-testid="admin-assign-confirm">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatCard({ label, value, icon: Icon, tone, testId }) {
  return (
    <div
      className="qb-card p-3 flex items-center justify-between"
      data-testid={testId}
    >
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold mt-1">{value}</div>
      </div>
      {Icon && (
        <Icon
          size={24}
          weight="fill"
          style={{ color: tone === "destructive" ? "hsl(var(--destructive))" : "hsl(var(--primary))" }}
        />
      )}
    </div>
  );
}
